/* =========================================================================
   useTelemetryConfirm — reusable hook to manage confirmation dialog state & actions
   for workspace tab and widget panel deletions.
   ========================================================================= */

import { useContext, useState, useCallback } from 'react';
import { CommandCenterStateProviderContext } from '../../../../context';
import { ConfirmDialogState } from '../lib/dashboard-types';

export type { ConfirmDialogState };

const defaultConfirmState: ConfirmDialogState = {
  open: false,
  type: 'delete',
  title: '',
  description: '',
  confirmText: 'Delete',
  cancelText: 'Cancel',
  onConfirmAction: () => { },
};

export function useTelemetryConfirm() {
  const context = useContext(CommandCenterStateProviderContext);
  const [localConfirmState, setLocalConfirmState] = useState<ConfirmDialogState>(defaultConfirmState);

  const confirmState: ConfirmDialogState = context?.telemetryConfirmState || localConfirmState;
  const setConfirmState = (context ? context.setTelemetryConfirmState : setLocalConfirmState) as React.Dispatch<React.SetStateAction<ConfirmDialogState>>;

  const closeConfirmDialog = useCallback(() => {
    setConfirmState((prev) => ({ ...(prev || defaultConfirmState), open: false }));
  }, [setConfirmState]);

  const handleConfirm = useCallback(() => {
    if (confirmState && confirmState.onConfirmAction) {
      confirmState.onConfirmAction();
    }
    closeConfirmDialog();
  }, [confirmState, closeConfirmDialog]);

  const confirmWidgetDeletion = useCallback((widgetName: string, onConfirm: () => void) => {
    setConfirmState({
      open: true,
      type: 'delete',
      title: 'Delete Panel?',
      description: `Are you sure you want to remove "${widgetName || 'this panel'}" from the workspace?`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      onConfirmAction: onConfirm,
    });
  }, [setConfirmState]);

  const confirmWorkspaceDeletion = useCallback((workspaceName: string, onConfirm: () => void) => {
    setConfirmState({
      open: true,
      type: 'delete',
      title: 'Delete Workspace?',
      description: `Are you sure you want to remove "${workspaceName || 'this workspace'}" from the dashboard?`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      onConfirmAction: onConfirm,
    });
  }, [setConfirmState]);

  const confirmParamDeletion = useCallback((paramName: string, onConfirm: () => void) => {
    setConfirmState({
      open: true,
      type: 'delete',
      title: 'Remove Parameter?',
      description: `Are you sure you want to remove "${paramName || 'this parameter'}" from the panel?`,
      confirmText: 'Remove',
      cancelText: 'Cancel',
      onConfirmAction: onConfirm,
    });
  }, [setConfirmState]);

  const confirmUnsavedChanges = useCallback(
    (onSave: () => void | Promise<void>, onDiscard: () => void) => {
      setConfirmState({
        open: true,
        type: 'update',
        title: 'Unsaved Changes',
        description: 'You have unsaved changes in your Telemetry Dashboard. Do you want to save before leaving?',
        confirmText: 'Save Dashboard',
        cancelText: "Don't Save",
        confirmLoadingText: 'Saving',
        onConfirmAction: async () => {
          await onSave();
          closeConfirmDialog();
        },
        onDiscardAction: () => {
          onDiscard();
          closeConfirmDialog();
        },
        onCancelAction: closeConfirmDialog,
      });
    },
    [setConfirmState, closeConfirmDialog]
  );

  return {
    confirmState,
    closeConfirmDialog,
    handleConfirm,
    confirmWidgetDeletion,
    confirmWorkspaceDeletion,
    confirmParamDeletion,
    confirmUnsavedChanges,
  };
}

export default useTelemetryConfirm;
